import { supabase } from '../supabaseClient';
import { nombreSeguroStorage } from './nombre-storage';

// Firmas (PNG) y fotos (JPEG) de los permisos de trabajo. Bucket PRIVADO
// (20261019020000): ruta {company_id}/{permit_id}/firmas|fotos/{archivo}; la
// primera carpeta es la empresa, que es lo que revisan las políticas wpe_*.
// En la base se guarda la ruta, nunca una URL; para ver se firma la URL.
export const BUCKET_EVIDENCIA_PERMISOS = 'work-permit-evidence';
export const SEGUNDOS_URL_FIRMADA = 120;
export const MAX_BYTES_EVIDENCIA = 5 * 1024 * 1024;
export const TIPOS_EVIDENCIA = ['image/png', 'image/jpeg'] as const;

export type CarpetaEvidencia = 'firmas' | 'fotos';

export function rutaEvidencia(companyId: string, permitId: string, carpeta: CarpetaEvidencia, ahora = Date.now()): string {
  const ext = carpeta === 'firmas' ? 'png' : 'jpg';
  const azar = Math.random().toString(36).slice(2, 10);
  return `${companyId}/${permitId}/${carpeta}/${nombreSeguroStorage(`${ahora}_${azar}.${ext}`)}`;
}

// Mensaje de error o null si el archivo se puede subir.
export function validarEvidencia(blob: Blob): string | null {
  if (!(TIPOS_EVIDENCIA as readonly string[]).includes(blob.type)) return 'Solo se aceptan imágenes PNG o JPEG.';
  if (blob.size > MAX_BYTES_EVIDENCIA) return 'La imagen supera el máximo de 5 MB.';
  return null;
}

export async function subirEvidencia(path: string, blob: Blob): Promise<string | null> {
  const error = validarEvidencia(blob);
  if (error) return error;
  const { error: upErr } = await supabase.storage
    .from(BUCKET_EVIDENCIA_PERMISOS)
    .upload(path, blob, { contentType: blob.type, upsert: false });
  return upErr ? upErr.message : null;
}

export async function urlsFirmadas(paths: string[]): Promise<Record<string, string>> {
  if (paths.length === 0) return {};
  const { data } = await supabase.storage
    .from(BUCKET_EVIDENCIA_PERMISOS)
    .createSignedUrls(paths, SEGUNDOS_URL_FIRMADA);
  const map: Record<string, string> = {};
  (data ?? []).forEach(d => { if (d.path && d.signedUrl) map[d.path] = d.signedUrl; });
  return map;
}

export async function borrarEvidencias(paths: string[]): Promise<void> {
  if (paths.length === 0) return;
  await supabase.storage.from(BUCKET_EVIDENCIA_PERMISOS).remove(paths);
}

// Para el PDF: descarga con la sesión (respeta las políticas) y devuelve data URL.
export async function evidenciaComoDataUrl(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage.from(BUCKET_EVIDENCIA_PERMISOS).download(path);
  if (error || !data) return null;
  return blobADataUrl(data);
}

export function blobADataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

// Medidas finales: el lado mayor queda en maxLado como máximo, sin agrandar.
export function medidasReducidas(ancho: number, alto: number, maxLado: number): { ancho: number; alto: number } {
  const mayor = Math.max(ancho, alto);
  if (mayor <= maxLado) return { ancho, alto };
  const f = maxLado / mayor;
  return { ancho: Math.round(ancho * f), alto: Math.round(alto * f) };
}

// Las fotos del celular pesan varios MB: se pasan a JPEG de 1600 px como máximo.
// Redibujar en un canvas también quita los metadatos (EXIF, ubicación).
export async function reducirFoto(origen: Blob, maxLado = 1600, calidad = 0.85): Promise<Blob> {
  const url = URL.createObjectURL(origen);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('No se pudo leer la imagen.'));
      i.src = url;
    });
    const { ancho, alto } = medidasReducidas(img.naturalWidth, img.naturalHeight, maxLado);
    return await canvasAJpeg(ancho, alto, ctx => ctx.drawImage(img, 0, 0, ancho, alto), calidad);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function canvasAJpeg(
  ancho: number, alto: number, dibujar: (ctx: CanvasRenderingContext2D) => void, calidad = 0.85,
): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = ancho;
  canvas.height = alto;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.reject(new Error('El navegador no permite procesar la imagen.'));
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, ancho, alto);
  dibujar(ctx);
  return new Promise((resolve, reject) => {
    canvas.toBlob(b => (b ? resolve(b) : reject(new Error('No se pudo procesar la imagen.'))), 'image/jpeg', calidad);
  });
}
